import { useEffect, useState, type Ref } from 'react';
import { Button } from '../atoms/Button';
import { Icon } from '../atoms/Icon';
import { Input } from '../atoms/Input';
import styles from './NumberStepper.module.css';

export interface NumberStepperProps {
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Decimal places accepted and shown (default 0). */
  decimals?: number;
  /** Unit after the number, e.g. `kg`. */
  unit?: string;
  /** Accessible labels of the buttons (no built-in texts, i18n). */
  labels: { decrement: string; increment: string };
  /** BCP 47 locale for the decimal separator (default `de-DE`). */
  locale?: string;
  id?: string;
  invalid?: boolean;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  'aria-describedby'?: string;
  'aria-label'?: string;
  size?: 'sm' | 'md' | 'lg';
  ref?: Ref<HTMLInputElement>;
}

function format(value: number | null, decimals: number, locale: string): string {
  if (value === null || Number.isNaN(value)) return '';
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: decimals,
    useGrouping: false,
  }).format(value);
}

/** Parses "1,5" as well as "1.5". Returns `undefined` for invalid input. */
export function parseDecimal(text: string): number | null | undefined {
  const t = text.trim().replace(/\s/g, '').replace(',', '.');
  if (t === '') return null;
  if (!/^-?\d*\.?\d*$/.test(t) || t === '.' || t === '-') return undefined;
  return Number(t);
}

/**
 * Number input with − / + buttons and an optional unit. Accepts the
 * locale's decimal comma; the value is a number (or `null` when empty).
 */
export function NumberStepper({
  value,
  onChange,
  min,
  max,
  step = 1,
  decimals = 0,
  unit,
  labels,
  locale = 'de-DE',
  size = 'md',
  disabled,
  ...rest
}: NumberStepperProps) {
  const [text, setText] = useState(() => format(value, decimals, locale));

  // Follow external changes unless they match what is typed.
  useEffect(() => {
    const parsed = parseDecimal(text);
    if (parsed !== value) setText(format(value, decimals, locale));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, decimals, locale]);

  const clamp = (v: number) => {
    const factor = 10 ** decimals;
    let r = Math.round(v * factor) / factor;
    if (min !== undefined) r = Math.max(min, r);
    if (max !== undefined) r = Math.min(max, r);
    return r;
  };
  const bump = (direction: 1 | -1) => {
    const next = clamp((value ?? min ?? 0) + direction * step);
    setText(format(next, decimals, locale));
    onChange(next);
  };

  return (
    <span className={[styles.stepper, styles[size]].join(' ')}>
      <Button
        variant="secondary"
        size={size === 'lg' ? 'md' : 'sm'}
        aria-label={labels.decrement}
        disabled={disabled || (min !== undefined && value !== null && value <= min)}
        onClick={() => bump(-1)}
        iconStart={<Icon name="minus" size="sm" />}
        className={styles.button}
        tabIndex={-1}
      />
      <Input
        {...rest}
        size={size}
        disabled={disabled}
        inputMode={decimals > 0 ? 'decimal' : 'numeric'}
        autoComplete="off"
        value={text}
        className={styles.input}
        onChange={(e) => {
          setText(e.target.value);
          const parsed = parseDecimal(e.target.value);
          if (parsed !== undefined) onChange(parsed);
        }}
        onBlur={() => setText(format(value, decimals, locale))}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            bump(1);
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            bump(-1);
          }
        }}
        endAdornment={unit ? <span className={styles.unit}>{unit}</span> : undefined}
      />
      <Button
        variant="secondary"
        size={size === 'lg' ? 'md' : 'sm'}
        aria-label={labels.increment}
        disabled={disabled || (max !== undefined && value !== null && value >= max)}
        onClick={() => bump(1)}
        iconStart={<Icon name="plus" size="sm" />}
        className={styles.button}
        tabIndex={-1}
      />
    </span>
  );
}
